/**
 * Angebots-Builder, Neugestaltung nach Mockup v3 (von Antoine am 28.09.2026 freigegeben): die
 * rechte Spalte „Prüfen & senden" und der Fuß des Dokuments.
 *
 * Hier wird NICHTS gerechnet und nichts geraten. Jede Zeile zeigt einen Wert, den der Builder
 * ohnehin schon hat:
 *   - „Bereit zum Senden" = `sendeReifeItems` aus `builder.tsx` (dieselbe Liste, die den
 *     Senden-Knopf beschriftet und im Senden-Dialog steht),
 *   - „MAESTRO hat geprüft" = feste Regeln über vorhandene Daten (LMIV-Angaben der
 *     Katalogpositionen, die Belegung aus `/api/events/:id/angefragte-tage`). Keine KI.
 *
 * Bewusst NICHT gebaut, obwohl das Mockup sie zeigt: „Passt zum Budget" und „Passt zum
 * Zeitrahmen". Für beides gibt es keine verlässliche Datengrundlage — das Budget steht nur als
 * Freitext in der Anfrage (`aiMeta.targetPerGuestCents` wird seit dem Komponisten nicht mehr
 * geschrieben und war ohnehin oft ein Paket-Median, kein Kundenwunsch), einen Zeitrahmen kennt
 * das Datenmodell gar nicht. Eine Prüfzeile ohne Grundlage wäre eine Behauptung.
 */
import type { ReactNode } from "react";
import { useApi } from "../hooks/useApi";
import { Card, Icon, StatusPill } from "./components";
import { formatDate, money } from "./format";
import type { TagesBelegung } from "./AngefragteTageKarte";
import { alsDatum, istVergangen } from "./AngefragteTageKarte";
import type { PriceBreakdown } from "@maestro/pricing";
import { vatLabel } from "../lib/builder-helpers";
import { A_LA_CARTE_KONDITIONEN_ENTFAELLT, A_LA_CARTE_VOR_ORT, NACH_KARTE } from "../lib/a-la-carte";

/** Eine Zeile der Sende-Reife (Form wie `sendeReifeItems` in builder.tsx). */
export interface ReifePunkt {
  key: string;
  label: string;
  done: boolean;
  wert?: string;
  aktion?: { label: string; onClick: () => void };
}

/** Eine Prüfzeile „MAESTRO hat geprüft". */
export interface Pruefung {
  key: string;
  ok: boolean;
  titel: string;
  text?: string;
  aktion?: { label: string; onClick: () => void };
}

const KURZ = (iso: string) => alsDatum(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" });

/**
 * Antoine 14.10.2026, „Warnhinweis bei abweichenden Gästezahlen“ (Fall Taufe: Vorgang 32, Variante
 * 41 — gerechnet wird mit der Variante). Abweichen darf sie bewusst, deshalb nur ein Hinweis.
 */
export function gaestezahlPruefung(vorgang: number | null | undefined, variante: number | undefined, onPruefen: () => void): Pruefung | null {
  if (vorgang == null || variante == null || vorgang === variante) return null;
  return {
    key: "gaeste",
    ok: false,
    titel: "Gästezahl weicht ab",
    text: `Vorgang ${vorgang} Gäste, diese Variante rechnet mit ${variante}.`,
    aktion: { label: "Prüfen", onClick: onPruefen },
  };
}

/**
 * Die Tagesprüfung aus derselben Quelle wie die Karte „Angefragter Tag". Vergangene Tage zählen
 * nicht — dort sagt „belegt" nichts mehr.
 */
export function useTagesPruefung(
  eventId: string | undefined,
  aktionen: { onDatumFestlegen: () => void; onTagAnsehen: () => void },
): Pruefung | null {
  const { data } = useApi<{ tage: TagesBelegung[] }>(eventId ? `/api/events/${eventId}/angefragte-tage` : null);
  if (!data) return null;
  if (data.tage.length === 0) {
    return {
      key: "tag",
      ok: false,
      titel: "Datum offen",
      text: "Ohne Datum lässt sich nicht prüfen, ob der Tag frei ist.",
      aktion: { label: "Festlegen", onClick: aktionen.onDatumFestlegen },
    };
  }
  const heute = new Date();
  const belegt = data.tage.filter((t) => !istVergangen(t.datum, heute) && t.anfragen + t.buchungen > 0);
  if (belegt.length === 0) {
    return { key: "tag", ok: true, titel: "Tag frei", text: `Keine weitere Veranstaltung am ${data.tage.map((t) => KURZ(t.datum)).join(", ")}.` };
  }
  const t = belegt[0]!;
  const anzahl = t.anfragen + t.buchungen;
  const erste = t.parallel?.[0];
  return {
    key: "tag",
    ok: false,
    titel: `${KURZ(t.datum)}: ${anzahl === 1 ? "1 weiteres Event" : `${anzahl} weitere Events`}`,
    text: erste ? `${erste.titel}${erste.gaeste != null ? `, ${erste.gaeste} Gäste` : ""}${anzahl > 1 ? " u. a." : ""}` : undefined,
    aktion: { label: "Tag ansehen", onClick: aktionen.onTagAnsehen },
  };
}

function Aktion({ aktion }: { aktion?: { label: string; onClick: () => void } }) {
  if (!aktion) return null;
  return (
    <button type="button" onClick={aktion.onClick} className="shrink-0 text-[12px] font-semibold text-primary hover:underline dark:text-primary-fixed-dim">
      {aktion.label}
    </button>
  );
}

/** „Bereit zum Senden x von y" + „MAESTRO hat geprüft" + (als Kinder) der Senden-Knopf. */
export function BereitZumSendenKarte({ punkte, pruefungen, children }: { punkte: ReifePunkt[]; pruefungen: Pruefung[]; children?: ReactNode }) {
  const fertig = punkte.filter((p) => p.done).length;
  return (
    <Card className="space-y-4 p-5" data-testid="builder-bereit-zum-senden">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-beleg-panel text-on-background dark:text-inverse-on-surface">Bereit zum Senden</h3>
        <StatusPill meta={{ label: `${fertig} von ${punkte.length}`, tone: fertig === punkte.length ? "success" : "warn" }} />
      </div>
      <ul className="space-y-1.5">
        {punkte.map((p) => (
          <li key={p.key} className="flex items-start gap-2 text-[12.5px]" data-testid={`sende-reife-${p.key}`}>
            <Icon
              name={p.done ? "check_circle" : "radio_button_unchecked"}
              className={`mt-px text-[16px] ${p.done ? "text-[#6F675A] dark:text-[#b8ae9e]" : "text-[#6F675A] dark:text-[#b8ae9e]"}`}
            />
            <span className="min-w-0 flex-1 text-on-surface-variant dark:text-secondary-fixed-dim">
              {p.label}
              {p.wert && <span className="ml-1 text-on-background dark:text-inverse-on-surface">{p.wert}</span>}
            </span>
            <Aktion aktion={p.aktion} />
          </li>
        ))}
      </ul>
      {pruefungen.length > 0 && (
        <div className="space-y-2 border-t border-outline-variant/30 pt-4 dark:border-outline/30" data-testid="builder-maestro-geprueft">
          <h4 className="text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">MAESTRO hat geprüft</h4>
          <ul className="divide-y divide-outline-variant/25 dark:divide-outline/20">
            {pruefungen.map((p) => (
              <li key={p.key} className="flex items-start gap-2 py-2" data-testid={`pruefung-${p.key}`}>
                <Icon
                  name={p.ok ? "check_circle" : "error"}
                  className={`mt-px text-[16px] ${p.ok ? "text-[#6F675A] dark:text-[#b8ae9e]" : "text-[#6F675A] dark:text-[#b8ae9e]"}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">{p.titel}</span>
                  {p.text && <span className="block text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">{p.text}</span>}
                </span>
                <Aktion aktion={p.aktion} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {children}
    </Card>
  );
}

/** Eine zuklappbare Zeile („Tag", „Verlauf") — der Inhalt steht darunter, wie er heute aussieht. */
export function AufklappZeile({
  icon,
  titel,
  wert,
  offen,
  onUmschalten,
  testId,
  children,
}: {
  icon: string;
  titel: string;
  wert: string;
  offen: boolean;
  onUmschalten: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Card className="overflow-hidden">
        <button
          type="button"
          data-testid={testId}
          aria-expanded={offen}
          onClick={onUmschalten}
          className="flex w-full items-center gap-2.5 px-[18px] py-3 text-left transition-colors hover:bg-[#FBF6EC] dark:hover:bg-surface-variant/15"
        >
          <Icon name={icon} className="text-[18px] text-on-surface-variant dark:text-secondary-fixed-dim" />
          <span className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{titel}</span>
          <span className="ml-auto truncate text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">{wert}</span>
          <Icon name={offen ? "expand_less" : "expand_more"} className="text-[18px] text-on-surface-variant dark:text-secondary-fixed-dim" />
        </button>
      </Card>
      {offen && children}
    </div>
  );
}

const EYEBROW = "block text-[10.5px] font-semibold uppercase tracking-[0.07em] text-secondary-fixed-dim";

/**
 * Der Fuß des Dokuments — so, wie der Gast ihn liest: Zahlung, Gültig bis, die Abschnitts-
 * summen und der Gesamtbetrag. Alle Zahlen aus `breakdown` (packages/pricing), dieselben wie
 * rechts in „Gesamt". Zahlung und Gültig bis öffnen den Reiter „Zahlungs-Konditionen", in dem
 * beides gepflegt wird (Antoine: der Chip bleibt der Ort dafür).
 */
export function DokumentFuss({
  zahlung,
  gueltigBis,
  onKonditionen,
  sectionSubtotals,
  breakdown,
  gaeste,
  aLaCarte = false,
}: {
  /** Migration 731: „À la carte“ - kein Betrag, keine Zahlung aus MAESTRO. */
  aLaCarte?: boolean;
  zahlung: string | null | undefined;
  gueltigBis: string | null | undefined;
  onKonditionen: () => void;
  sectionSubtotals: { key: string; label: string; cents: number }[];
  breakdown: PriceBreakdown;
  gaeste: number;
}) {
  const netto = breakdown.vatBreakdown.reduce((s, b) => s + b.netCents, 0);
  const saetze = breakdown.vatBreakdown.filter((b) => b.grossCents > 0);
  return (
    <Card className="overflow-hidden" data-testid="builder-dokument-fuss">
      <div className="grid gap-4 border-b border-[#F2ECDF] px-[18px] py-4 dark:border-outline/20 sm:grid-cols-2">
        <button type="button" onClick={onKonditionen} className="min-w-0 rounded-md text-left hover:bg-[#FBF6EC] dark:hover:bg-surface-variant/15" title="Zahlungs-Konditionen öffnen">
          <span className={EYEBROW}>Zahlung</span>
          <span className="mt-0.5 block text-body-sm text-on-background dark:text-inverse-on-surface">{aLaCarte ? A_LA_CARTE_KONDITIONEN_ENTFAELLT : zahlung || "Standard des Betriebs"}</span>
        </button>
        <button type="button" onClick={onKonditionen} className="min-w-0 rounded-md text-left hover:bg-[#FBF6EC] dark:hover:bg-surface-variant/15" title="Gültigkeit in den Zahlungs-Konditionen ändern">
          <span className={EYEBROW}>Gültig bis</span>
          <span className="mt-0.5 block text-body-sm text-on-background dark:text-inverse-on-surface">{gueltigBis ? formatDate(gueltigBis) : "nicht festgelegt"}</span>
        </button>
      </div>
      {aLaCarte ? (
        <dl className="space-y-1.5 px-[18px] py-4 text-body-sm" data-testid="builder-dokument-fuss-a-la-carte">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-beleg-panel text-on-background dark:text-inverse-on-surface">Gesamt</dt>
            <dd className="text-beleg-summe text-on-background dark:text-inverse-on-surface">{NACH_KARTE}</dd>
          </div>
          <p className="text-[11.5px] text-on-surface-variant dark:text-secondary-fixed-dim">{A_LA_CARTE_VOR_ORT}</p>
        </dl>
      ) : (
      <dl className="space-y-1.5 px-[18px] py-4 text-body-sm">
        {sectionSubtotals.map((s) => (
          <div key={s.key} className="flex items-baseline justify-between gap-3 text-on-surface-variant dark:text-secondary-fixed-dim">
            <dt>{s.label}</dt>
            <dd className="tabular-nums">{money(s.cents)}</dd>
          </div>
        ))}
        {breakdown.discountCents > 0 && (
          <div className="flex items-baseline justify-between gap-3 text-on-surface-variant dark:text-secondary-fixed-dim">
            <dt>Rabatt</dt>
            <dd className="tabular-nums">− {money(breakdown.discountCents)}</dd>
          </div>
        )}
        {breakdown.adjustmentCents !== 0 && (
          <div className="flex items-baseline justify-between gap-3 text-on-surface-variant dark:text-secondary-fixed-dim">
            <dt>Preisanpassung</dt>
            <dd className="tabular-nums">{money(breakdown.adjustmentCents)}</dd>
          </div>
        )}
        <div className="flex items-baseline justify-between gap-3 border-t border-[#F2ECDF] pt-2 dark:border-outline/20">
          <dt className="text-beleg-panel text-on-background dark:text-inverse-on-surface">Gesamt</dt>
          <dd className="text-beleg-summe tabular-nums text-on-background dark:text-inverse-on-surface">{money(breakdown.amountTotalCents)}</dd>
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-[11.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
          <span>
            {saetze.length > 0
              ? `inkl. ${saetze.map((b) => `${vatLabel(b.rateBp)} USt ${money(b.vatCents)}`).join(", ")} · netto ${money(netto)}`
              : ""}
          </span>
          {gaeste > 0 && <span>{money(breakdown.perPersonCents)} pro Gast</span>}
        </div>
      </dl>
      )}
    </Card>
  );
}
